import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, videos } from "@/mocks/videos";

const siteUrl = "https://www.jiuseshipin.com";

const schemaData = (catName: string, catSlug: string) => [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${catName} - 九色视频`,
    "description": `九色视频${catName}分类页面，汇聚海量高清${catName}视频资源，极速播放，随时在线观看精彩${catName}内容。`,
    "url": `${siteUrl}/category/${catSlug}`,
    "isPartOf": { "@type": "WebSite", "name": "九色视频", "url": siteUrl },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": catName, "item": `${siteUrl}/category/${catSlug}` },
    ],
  },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || "dianying");

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeTab]);

  const currentCat = categories.find((c) => c.slug === activeTab);
  const catVideos = videos.filter((v) => v.category === activeTab);

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.history.pushState(null, "", `/category/${newSlug}`);
  };

  if (!currentCat) {
    return (
      <>
        <Navbar />
        <div className="pt-24 text-center py-20">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${currentCat.name} - 九色视频高清在线观看`}
        description={`九色视频${currentCat.name}分类页面，汇聚海量高清${currentCat.name}视频资源，极速播放，随时在线观看精彩${currentCat.name}内容。涵盖最新热门${currentCat.name}推荐。`}
        keywords={`九色视频,${currentCat.name},${currentCat.name}高清,${currentCat.name}在线观看,高清视频播放,九色视频${currentCat.name}`}
        canonicalUrl={`${siteUrl}/category/${currentCat.slug}`}
        schemaData={schemaData(currentCat.name, currentCat.slug)}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: currentCat.name },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-4 mb-2">
              <div className={`w-10 h-10 rounded-xl ${currentCat.color} flex items-center justify-center`}>
                <i className={`${currentCat.icon} text-lg`}></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {currentCat.name}
              </h1>
            </div>
            <p className="text-foreground-600 ml-14">
              探索{currentCat.name}分类下的所有精彩视频内容，实时更新，极速播放。
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-10 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange(cat.slug);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          {catVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16" data-product-shop>
              {catVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-100 rounded-2xl overflow-hidden transition-all duration-300 hover:translate-y-[-4px] cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-primary-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                        <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
                      </div>
                    </div>
                    <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded-md font-medium">
                      {video.duration}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span><i className="ri-eye-line mr-1"></i>{video.views}</span>
                      <span><i className="ri-star-fill text-accent-500 mr-0.5"></i>{video.rating}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无视频内容</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}